import React, { FC, useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import { createStyles, makeStyles, Theme } from "@material-ui/core/styles";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import CloudUploadOutlinedIcon from "@material-ui/icons/CloudUploadOutlined";
import CopyToClipboard from "modus/components/misc/CopyToClipboard";
import { FormattedMessage } from "react-intl";
import UploadCSRDialog from "./UploadCSRDialog";
import { IdentityApplicationViewState } from "libs/resources/identity/applications/application-views/IdentityApplicationInfo";

export interface MtlsCertificateRow {
  fingerprint: string;
  uploaded: string;
  expires: string;
  status: "active" | "expired";
}

export interface UploadCSRProps {
  certificates?: MtlsCertificateRow[];
  onUploadCsr?: () => void;
  application?: IdentityApplicationViewState["application"];
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    content: {
      padding: theme.spacing(3),
    },
    headerRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: theme.spacing(2),
      marginBottom: theme.spacing(2.5),
    },
    banner: {
      display: "flex",
      alignItems: "stretch",
      gap: theme.spacing(1.5),
      backgroundColor: "#D9EAF7",
      border: "1px solid #0B67A3",
      borderLeftWidth: 10,
      borderRadius: 8,
      padding: theme.spacing(2),
      marginBottom: theme.spacing(3),
    },
    bannerIcon: {
      color: "#18323F",
      marginTop: 2,
      flexShrink: 0,
    },
    tableWrapper: {
      border: "1px solid #D0D6E0",
      borderRadius: 4,
      overflow: "hidden",
      backgroundColor: theme.palette.common.white,
    },
    emptyState: {
      border: "1px solid #D0D6E0",
      borderRadius: 4,
      backgroundColor: theme.palette.common.white,
      padding: theme.spacing(4, 3),
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: theme.spacing(2),
      minHeight: 280,
      textAlign: "center",
    },
    emptyStateImage: {
      width: "100%",
      maxWidth: 640,
      height: 240,
    },
    emptyStateText: {
      color: "#31363F",
      fontSize: theme.typography.pxToRem(18),
    },
    tableHeadCell: {
      backgroundColor: "#F4F6FA",
      color: "#31363F",
      fontWeight: theme.typography.fontWeightBold,
      fontSize: theme.typography.pxToRem(15),
      borderBottom: "1px solid #D0D6E0",
      paddingTop: theme.spacing(1.75),
      paddingBottom: theme.spacing(1.75),
    },
    tableBodyCell: {
      color: "#424A57",
      borderBottom: "1px solid #E3E7EE",
      fontSize: theme.typography.pxToRem(14),
      paddingTop: theme.spacing(1.5),
      paddingBottom: theme.spacing(1.5),
    },
    fingerprintCell: {
      fontFamily: '"Roboto Mono", monospace',
      letterSpacing: 0.2,
      wordBreak: "break-word",
    },
    certificateCell:{
      maxWidth: '500px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    statusChip: {
      fontWeight: theme.typography.fontWeightMedium,
      minWidth: 72,
    },
    activeChip: {
      backgroundColor: "#E6F2DE",
      color: "#1E7A2F",
    },
    expiredChip: {
      backgroundColor: "#F8DDE0",
      color: "#C2252D",
    },
    uploadButton: {
      minWidth: 148,
      height: 42,
      textTransform: "none",
      fontSize: theme.typography.pxToRem(16),
      fontWeight: theme.typography.fontWeightMedium,
      borderRadius: 10,
      boxShadow: "none",
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },
    title: {
      color: "#31363F",
      fontWeight: theme.typography.fontWeightMedium,
    },
  })
);

const UploadCSR: FC<UploadCSRProps> = ({ onUploadCsr, application }) => {
  const classes = useStyles();
  const [uploadDialogOpen, setUploadDialogOpen] = useState<boolean>(false);
  const [currentApplication, setCurrentApplication] = useState(application);
  const certificateRows = currentApplication?.certificate ?? [];
  const showEmptyState = currentApplication?.certificate == null;

  useEffect(() => {
    setCurrentApplication(application);
  }, [application]);

  const openUploadDialog = useCallback(() => {
    setUploadDialogOpen(true);
  }, []);

  const closeUploadDialog = useCallback(() => {
    setUploadDialogOpen(false);
  }, []);

  const handleUpload = useCallback((nextApplication?: IdentityApplicationViewState["application"]) => {
    if (nextApplication) {
      setCurrentApplication(nextApplication);
    }

    onUploadCsr?.();
  }, [onUploadCsr]);

  return (
    <Box mt={3}>
    <Paper>
      <Container maxWidth={false} disableGutters className={classes.content}>
        <Box className={classes.headerRow}>
          <Typography variant="h6" className={classes.title}>
            <FormattedMessage defaultMessage="mTLS Certificates" />
          </Typography>
          <Button
            variant="contained"
            color="primary"
            className={classes.uploadButton}
            startIcon={<CloudUploadOutlinedIcon />}
            onClick={openUploadDialog}
          >
            <FormattedMessage defaultMessage="Upload CSR" />
          </Button>
        </Box>

        <Box className={classes.banner}>
          <InfoOutlinedIcon className={classes.bannerIcon} />
          <Typography variant="body1" style={{ color: "#334155", lineHeight: 1.6 }}>
            <FormattedMessage defaultMessage="Use X.509 certificate to certificate Based Authentication instead of TLS Client secrets. Configuration management is required before using certificate-based authentication." />
          </Typography>
        </Box>

        {showEmptyState ? (
          <Box className={classes.emptyState}>
            <Typography className={classes.emptyStateText}>
              Upload your first Certificate Signing Request here
            </Typography>
                          <Button
            variant="contained"
            color="primary"
            className={classes.uploadButton}
            startIcon={<CloudUploadOutlinedIcon />}
            onClick={openUploadDialog}
          >
            <FormattedMessage defaultMessage="Upload CSR" />
          </Button>
          </Box>
        ) : (
          <TableContainer className={classes.tableWrapper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell className={classes.tableHeadCell}>
                    <FormattedMessage defaultMessage="Fingerprint" />
                  </TableCell>
                  <TableCell className={classes.tableHeadCell}>
                    <FormattedMessage defaultMessage="Certificate" />
                  </TableCell>
                  <TableCell className={classes.tableHeadCell}>
                    <FormattedMessage defaultMessage="Action" />
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {certificateRows.map((certificate) => (
                  <TableRow key={certificate.fingerprint} hover>
                    <TableCell className={`${classes.tableBodyCell} ${classes.fingerprintCell}`}>
                      {certificate.fingerprint}
                    </TableCell>
                    <TableCell className={`${classes.tableBodyCell} ${classes.certificateCell}`}>
                      {certificate.certificate}
                    </TableCell>
                    <TableCell className={classes.certificateCell}>
                      <CopyToClipboard text={certificate.certificate} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Container>
    </Paper>
    <UploadCSRDialog open={uploadDialogOpen} onClose={closeUploadDialog} onUpload={handleUpload} application={application} />
    </Box>
  );
};

export default UploadCSR;

